import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  ExpenseSheetSchema,
  SaveExpenseDraftSchema,
  SignExpenseSchema,
  type ExpenseSheet,
  type ProcedureId,
  type SaveExpenseDraftInput,
  type SignExpenseInput,
} from '@datazo/contracts';
import { ApiClientError, apiFetch } from '@/shared/api/client';

/**
 * Hooks de acceso a `/api/expense`. Patrón espejo de `useConsentByProcedure`
 * y `useAnesthesiaByProcedure` — la hoja de gasto es legalmente vinculante.
 */

export const expenseKeys = {
  all: ['expense'] as const,
  byProcedure: (id: ProcedureId) => ['expense', id] as const,
};

export function useExpenseByProcedure(
  procedimientoId: ProcedureId | undefined,
): UseQueryResult<ExpenseSheet | null, Error> {
  return useQuery({
    queryKey:
      procedimientoId !== undefined
        ? expenseKeys.byProcedure(procedimientoId)
        : (['expense', '__missing__'] as const),
    queryFn: async (): Promise<ExpenseSheet | null> => {
      if (procedimientoId === undefined) {
        throw new Error('procedimientoId requerido');
      }
      try {
        return await apiFetch<ExpenseSheet>(
          `/api/expense/by-procedure/${encodeURIComponent(procedimientoId)}`,
          { parseAs: ExpenseSheetSchema },
        );
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: procedimientoId !== undefined,
    staleTime: 30_000,
  });
}

export function useSignExpense(): UseMutationResult<ExpenseSheet, Error, SignExpenseInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SignExpenseInput) => {
      const body = SignExpenseSchema.parse(input);
      return apiFetch<ExpenseSheet>('/api/expense/sign', {
        method: 'POST',
        body,
        parseAs: ExpenseSheetSchema,
      });
    },
    onSuccess: (created) => {
      qc.setQueryData(expenseKeys.byProcedure(created.procedimientoId), created);
      void qc.invalidateQueries({ queryKey: expenseKeys.byProcedure(created.procedimientoId) });
      void qc.invalidateQueries({ queryKey: ['procedures'] });
      void qc.invalidateQueries({ queryKey: ['procedures', created.procedimientoId] });
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

/** Preguardado del borrador (sin firma). No transiciona el episodio. */
export function useSaveExpenseDraft(): UseMutationResult<
  ExpenseSheet,
  Error,
  SaveExpenseDraftInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveExpenseDraftInput) => {
      const body = SaveExpenseDraftSchema.parse(input);
      return apiFetch<ExpenseSheet>('/api/expense/draft', {
        method: 'POST',
        body,
        parseAs: ExpenseSheetSchema,
      });
    },
    onSuccess: (saved) => {
      qc.setQueryData(expenseKeys.byProcedure(saved.procedimientoId), saved);
      void qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export type { ExpenseSheet };
